import _ from 'lodash';
import * as monaco from 'monaco-editor';
import type vscodeProtocol from 'vscode-languageserver-protocol/browser';
import { URI } from 'vscode-uri';

import { assertUnreachable } from './assertUnreachable';

export type OnException = (message: string) => void;

/**
 * create 2 way mapping for vscode to monaco enums, vscode enum value coverage is enforced by typescript
 * monaco requires a test to be written in packages/studio/src/convertVscodeToMonaco.test.ts
 */
export function enumMappingFactory<
  VscodeEnum extends number,
  MonacoEnum extends number,
>(
  vscodeToMonacoMapping: {
    [key in VscodeEnum]: MonacoEnum | [MonacoEnum, ...MonacoEnum[]];
  },
  defaultVscodeValue: VscodeEnum,
  defaultMonacoValue: MonacoEnum,
) {
  const monacoToVscodeMapping = Object.fromEntries(
    Object.entries<MonacoEnum | [MonacoEnum, ...MonacoEnum[]]>(
      vscodeToMonacoMapping,
    ).flatMap(([vscodeValue, monacoValues]) =>
      (Array.isArray(monacoValues) ? monacoValues : [monacoValues]).map(
        (monacoValue) => [monacoValue, parseInt(vscodeValue, 10) as VscodeEnum],
      ),
    ),
  );

  return {
    monacoToVscode: (
      monacoValue: MonacoEnum,
      onException: OnException,
    ): VscodeEnum => {
      const vscodeValue = monacoToVscodeMapping[monacoValue];

      if (vscodeValue === undefined) {
        onException(`No mapping found for ${monacoValue}`);
        return defaultVscodeValue;
      }
      return vscodeValue;
    },
    vscodeToMonaco: (
      vscodeValue: VscodeEnum,
      onException: OnException,
    ): MonacoEnum => {
      const monacoValue = vscodeToMonacoMapping[vscodeValue];

      if (monacoValue === undefined) {
        onException(`No mapping found for ${vscodeValue}`);
        return defaultMonacoValue;
      }
      return (
        Array.isArray(monacoValue) ? monacoValue[0] : monacoValue
      ) as MonacoEnum;
    },
  };
}

export const completionItemTagMapping = enumMappingFactory<
  vscodeProtocol.CompletionItemTag,
  monaco.languages.CompletionItemTag
>(
  {
    [1 satisfies typeof vscodeProtocol.CompletionItemTag.Deprecated]:
      monaco.languages.CompletionItemTag.Deprecated,
  },
  1 satisfies typeof vscodeProtocol.CompletionItemTag.Deprecated,
  monaco.languages.CompletionItemTag.Deprecated,
);

export const insertTextFormatMapping = enumMappingFactory<
  vscodeProtocol.InsertTextFormat,
  monaco.languages.CompletionItemInsertTextRule
>(
  {
    [1 satisfies typeof vscodeProtocol.InsertTextFormat.PlainText]: [
      monaco.languages.CompletionItemInsertTextRule.KeepWhitespace,
      // TODO: does plaintext make sense to match to None?
      monaco.languages.CompletionItemInsertTextRule.None,
    ] as [
      monaco.languages.CompletionItemInsertTextRule,
      ...monaco.languages.CompletionItemInsertTextRule[],
    ],

    [2 satisfies typeof vscodeProtocol.InsertTextFormat.Snippet]:
      monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
  },
  1 satisfies typeof vscodeProtocol.InsertTextFormat.PlainText,
  monaco.languages.CompletionItemInsertTextRule.KeepWhitespace,
);

/**
 * mapping to go from vscode CompletionItemKind to monaco CompletionItemKind and back.
 * This is a lossy conversion. Monaco Constructor, Issue, User, and Customcolor all
 * get converted to vscode Constructor, but when converting back to monaco vscode
 * Constructor will always be converted to monaco Issue. This is because numerical
 * keys are sorted and issue is 26. We could add a way to work around this, but I'm not sure we
 * will be using Constructor so it might not be worth it?
 */
export const completionItemKindMapping = enumMappingFactory<
  vscodeProtocol.CompletionItemKind,
  monaco.languages.CompletionItemKind
>(
  {
    [1 satisfies typeof vscodeProtocol.CompletionItemKind.Text]:
      monaco.languages.CompletionItemKind.Text,

    [2 satisfies typeof vscodeProtocol.CompletionItemKind.Method]:
      monaco.languages.CompletionItemKind.Method,

    [3 satisfies typeof vscodeProtocol.CompletionItemKind.Function]:
      monaco.languages.CompletionItemKind.Function,

    [4 satisfies typeof vscodeProtocol.CompletionItemKind.Constructor]: [
      monaco.languages.CompletionItemKind.Constructor,
      monaco.languages.CompletionItemKind.Issue,
      monaco.languages.CompletionItemKind.User,
      monaco.languages.CompletionItemKind.Customcolor,
    ] as [
      monaco.languages.CompletionItemKind,
      ...monaco.languages.CompletionItemKind[],
    ],

    [5 satisfies typeof vscodeProtocol.CompletionItemKind.Field]:
      monaco.languages.CompletionItemKind.Field,

    [6 satisfies typeof vscodeProtocol.CompletionItemKind.Variable]:
      monaco.languages.CompletionItemKind.Variable,

    [7 satisfies typeof vscodeProtocol.CompletionItemKind.Class]:
      monaco.languages.CompletionItemKind.Class,

    [8 satisfies typeof vscodeProtocol.CompletionItemKind.Interface]:
      monaco.languages.CompletionItemKind.Interface,

    [9 satisfies typeof vscodeProtocol.CompletionItemKind.Module]:
      monaco.languages.CompletionItemKind.Module,

    [10 satisfies typeof vscodeProtocol.CompletionItemKind.Property]:
      monaco.languages.CompletionItemKind.Property,

    [11 satisfies typeof vscodeProtocol.CompletionItemKind.Unit]:
      monaco.languages.CompletionItemKind.Unit,

    [12 satisfies typeof vscodeProtocol.CompletionItemKind.Value]:
      monaco.languages.CompletionItemKind.Value,

    [13 satisfies typeof vscodeProtocol.CompletionItemKind.Enum]:
      monaco.languages.CompletionItemKind.Enum,

    [14 satisfies typeof vscodeProtocol.CompletionItemKind.Keyword]:
      monaco.languages.CompletionItemKind.Keyword,

    [15 satisfies typeof vscodeProtocol.CompletionItemKind.Snippet]:
      monaco.languages.CompletionItemKind.Snippet,

    [16 satisfies typeof vscodeProtocol.CompletionItemKind.Color]:
      monaco.languages.CompletionItemKind.Color,

    [17 satisfies typeof vscodeProtocol.CompletionItemKind.File]:
      monaco.languages.CompletionItemKind.File,

    [18 satisfies typeof vscodeProtocol.CompletionItemKind.Reference]:
      monaco.languages.CompletionItemKind.Reference,

    [19 satisfies typeof vscodeProtocol.CompletionItemKind.Folder]:
      monaco.languages.CompletionItemKind.Folder,

    [20 satisfies typeof vscodeProtocol.CompletionItemKind.EnumMember]:
      monaco.languages.CompletionItemKind.EnumMember,

    [21 satisfies typeof vscodeProtocol.CompletionItemKind.Constant]:
      monaco.languages.CompletionItemKind.Constant,

    [22 satisfies typeof vscodeProtocol.CompletionItemKind.Struct]:
      monaco.languages.CompletionItemKind.Struct,

    [23 satisfies typeof vscodeProtocol.CompletionItemKind.Event]:
      monaco.languages.CompletionItemKind.Event,

    [24 satisfies typeof vscodeProtocol.CompletionItemKind.Operator]:
      monaco.languages.CompletionItemKind.Operator,

    [25 satisfies typeof vscodeProtocol.CompletionItemKind.TypeParameter]:
      monaco.languages.CompletionItemKind.TypeParameter,
  },
  1 satisfies typeof vscodeProtocol.CompletionItemKind.Text,
  monaco.languages.CompletionItemKind.Text,
);

export const completionTriggerKindMapping = enumMappingFactory<
  vscodeProtocol.CompletionTriggerKind,
  monaco.languages.CompletionTriggerKind
>(
  {
    [1 satisfies typeof vscodeProtocol.CompletionTriggerKind.Invoked]:
      monaco.languages.CompletionTriggerKind.Invoke,
    [2 satisfies typeof vscodeProtocol.CompletionTriggerKind.TriggerCharacter]:
      monaco.languages.CompletionTriggerKind.TriggerCharacter,
    [3 satisfies typeof vscodeProtocol.CompletionTriggerKind.TriggerForIncompleteCompletions]:
      monaco.languages.CompletionTriggerKind.TriggerForIncompleteCompletions,
  },
  1 satisfies typeof vscodeProtocol.CompletionTriggerKind.Invoked,
  monaco.languages.CompletionTriggerKind.Invoke,
);

export const diagnosticSeverityMapping = enumMappingFactory<
  vscodeProtocol.DiagnosticSeverity,
  monaco.MarkerSeverity
>(
  {
    [1 satisfies typeof vscodeProtocol.DiagnosticSeverity.Error]:
      monaco.MarkerSeverity.Error,
    [2 satisfies typeof vscodeProtocol.DiagnosticSeverity.Warning]:
      monaco.MarkerSeverity.Warning,
    [3 satisfies typeof vscodeProtocol.DiagnosticSeverity.Information]:
      monaco.MarkerSeverity.Info,
    [4 satisfies typeof vscodeProtocol.DiagnosticSeverity.Hint]:
      monaco.MarkerSeverity.Hint,
  },
  1 satisfies typeof vscodeProtocol.DiagnosticSeverity.Error,
  monaco.MarkerSeverity.Error,
);

export const codeActionTriggerKindMapping = enumMappingFactory<
  vscodeProtocol.CodeActionTriggerKind,
  monaco.languages.CodeActionTriggerType
>(
  {
    [1 satisfies typeof vscodeProtocol.CodeActionTriggerKind.Invoked]:
      monaco.languages.CodeActionTriggerType.Invoke,
    [2 satisfies typeof vscodeProtocol.CodeActionTriggerKind.Automatic]:
      monaco.languages.CodeActionTriggerType.Auto,
  },
  1 satisfies typeof vscodeProtocol.CodeActionTriggerKind.Invoked,
  monaco.languages.CodeActionTriggerType.Invoke,
);

export interface Mapper<
  VscodeType,
  MonacoType,
  VscodeArguments extends Array<unknown> = [],
  MonacoArguments extends Array<unknown> = [],
  ReturnVscodeValue = VscodeType,
  ReturnMonacoValue = MonacoType,
> {
  vscodeToMonaco: (
    value: VscodeType,
    ...rest: VscodeArguments
  ) => ReturnMonacoValue;
  monacoToVscode: (
    value: MonacoType,
    ...rest: MonacoArguments
  ) => ReturnVscodeValue;
}

export const positionMapping: Mapper<
  vscodeProtocol.Position,
  monaco.IPosition
> = {
  vscodeToMonaco(position) {
    return { lineNumber: position.line + 1, column: position.character + 1 };
  },
  monacoToVscode(position) {
    return { line: position.lineNumber - 1, character: position.column - 1 };
  },
};

export const rangeMapping: Mapper<vscodeProtocol.Range, monaco.IRange> = {
  vscodeToMonaco: (range) => {
    const start = positionMapping.vscodeToMonaco(range.start);
    const end = positionMapping.vscodeToMonaco(range.end);
    return {
      endColumn: end.column,
      endLineNumber: end.lineNumber,
      startColumn: start.column,
      startLineNumber: start.lineNumber,
    };
  },
  monacoToVscode: (range) => ({
    start: positionMapping.monacoToVscode({
      lineNumber: range.startLineNumber,
      column: range.startColumn,
    }),
    end: positionMapping.monacoToVscode({
      lineNumber: range.endLineNumber,
      column: range.endColumn,
    }),
  }),
};

export const markupMapping: Mapper<
  vscodeProtocol.MarkupContent | vscodeProtocol.MarkedString,
  monaco.IMarkdownString,
  [],
  [],
  vscodeProtocol.MarkupContent
> = {
  vscodeToMonaco: (documentation) => ({
    value:
      typeof documentation === 'string' ? documentation : documentation.value,
    isTrusted: true,
  }),
  monacoToVscode: (documentation) => ({
    value:
      typeof documentation === 'string' ? documentation : documentation.value,
    kind: 'markdown',
  }),
};

export const relatedInformationMapping: Mapper<
  vscodeProtocol.DiagnosticRelatedInformation,
  monaco.editor.IRelatedInformation
> = {
  vscodeToMonaco(relatedInformation) {
    const monacoLocation = locationMapper.vscodeToMonaco(
      relatedInformation.location,
    );
    return {
      ...monacoLocation.range,
      message: relatedInformation.message,
      resource: monacoLocation.uri,
    };
  },
  monacoToVscode(relatedInformation) {
    return {
      location: locationMapper.monacoToVscode({
        range: relatedInformation,
        uri: relatedInformation.resource,
      }),
      message: relatedInformation.message,
    };
  },
};

export const diagnosticsMapping: Mapper<
  vscodeProtocol.Diagnostic,
  monaco.editor.IMarkerData,
  [OnException],
  [OnException]
> = {
  // diagnostic.data is lost
  vscodeToMonaco(diagnostic, onException) {
    return {
      ...rangeMapping.vscodeToMonaco(diagnostic.range),
      message: diagnostic.message,
      severity: diagnostic.severity
        ? diagnosticSeverityMapping.vscodeToMonaco(
            diagnostic.severity,
            onException,
          )
        : monaco.MarkerSeverity.Error,
      source: diagnostic.source,
      tags: diagnostic.tags,
      relatedInformation: diagnostic.relatedInformation?.map((r) =>
        relatedInformationMapping.vscodeToMonaco(r),
      ),
      code:
        typeof diagnostic.code !== 'undefined'
          ? diagnostic.codeDescription === undefined
            ? String(diagnostic.code)
            : {
                value: String(diagnostic.code),
                target: URI.parse(diagnostic.codeDescription.href),
              }
          : undefined,
    };
  },
  // diagnostic.modelVersionId is lost
  monacoToVscode(diagnostic, onException) {
    const code = diagnostic.code;
    return {
      range: rangeMapping.monacoToVscode(diagnostic),
      message: diagnostic.message,
      severity: diagnosticSeverityMapping.monacoToVscode(
        diagnostic.severity,
        onException,
      ),
      source: diagnostic.source,
      tags: diagnostic.tags,
      relatedInformation: diagnostic.relatedInformation?.map((r) =>
        relatedInformationMapping.monacoToVscode(r),
      ),
      ...(typeof code === 'object'
        ? {
            code: code.value,
            codeDescription: { href: code.target.toString(true) },
          }
        : { code }),
    };
  },
};

export const completionItemMapping: Mapper<
  vscodeProtocol.CompletionItem,
  monaco.languages.CompletionItem,
  [
    monaco.IRange | monaco.languages.CompletionItemRanges,
    string[] | undefined,
    OnException,
  ],
  [OnException]
> = {
  vscodeToMonaco(item, range, allCommitCharacters, onException) {
    return {
      label: {
        label: item.label,
        ...item.labelDetails,
      },
      insertText: item.insertText ?? item.label,
      range,
      kind: completionItemKindMapping.vscodeToMonaco(
        item.kind ??
          (1 satisfies typeof vscodeProtocol.CompletionItemKind.Text),
        onException,
      ),
      detail: item.detail,
      documentation: item.documentation
        ? markupMapping.vscodeToMonaco(item.documentation)
        : undefined,
      sortText: item.sortText,
      filterText: item.filterText,
      insertTextRules: item.insertTextFormat
        ? insertTextFormatMapping.vscodeToMonaco(
            item.insertTextFormat,
            onException,
          )
        : undefined,
      commitCharacters: item.commitCharacters ?? allCommitCharacters,
      preselect: item.preselect,
      tags: item.tags
        ? item.tags.map((tag) =>
            completionItemTagMapping.vscodeToMonaco(tag, onException),
          )
        : item.tags,
      additionalTextEdits: item.additionalTextEdits?.map((edit) => ({
        range: rangeMapping.vscodeToMonaco(edit.range),
        text: edit.newText,
      })),
    };
  },
  monacoToVscode(item, onException) {
    const { label, ...labelDetails } =
      typeof item.label === 'string' ? { label: item.label } : item.label;
    return {
      label,
      labelDetails: _.isEmpty(labelDetails) ? undefined : labelDetails,
      kind: completionItemKindMapping.monacoToVscode(item.kind, onException),
      documentation:
        typeof item.documentation === 'object'
          ? markupMapping.monacoToVscode(item.documentation)
          : item.documentation,
      insertTextFormat: item.insertTextRules
        ? insertTextFormatMapping.monacoToVscode(
            item.insertTextRules,
            onException,
          )
        : undefined,
      tags: item.tags?.map((tag) =>
        completionItemTagMapping.vscodeToMonaco(tag, onException),
      ),
      additionalTextEdits: item.additionalTextEdits?.map((edit) => ({
        range: rangeMapping.monacoToVscode(edit.range),
        newText: edit.text ?? '',
      })),
      sortText: item.sortText,
      filterText: item.filterText,
      commitCharacters: item.commitCharacters,
      preselect: item.preselect,
      insertText: item.insertText,
      detail: item.detail,
    };
  },
};

export const hoverMapper: Mapper<vscodeProtocol.Hover, monaco.languages.Hover> =
  {
    vscodeToMonaco(hover) {
      return {
        contents: (Array.isArray(hover.contents)
          ? hover.contents
          : [hover.contents]
        ).map((h) => markupMapping.vscodeToMonaco(h)),
        range: hover.range && rangeMapping.vscodeToMonaco(hover.range),
      };
    },
    monacoToVscode(hover) {
      return {
        contents: hover.contents.map((h) => ({
          language: 'markdown',
          value: h.value,
        })),
        range: hover.range && rangeMapping.monacoToVscode(hover.range),
      };
    },
  };

export const semanticTokensMapper: Mapper<
  vscodeProtocol.SemanticTokens,
  monaco.languages.SemanticTokens
> = {
  vscodeToMonaco({ data, resultId }) {
    return { data: new Uint32Array(data), resultId };
  },
  monacoToVscode({ data, resultId }) {
    return { data: [...data], resultId };
  },
};

export const completionContextMapping: Mapper<
  vscodeProtocol.CompletionContext,
  monaco.languages.CompletionContext,
  [OnException],
  [OnException]
> = {
  monacoToVscode(context, onException) {
    return {
      triggerKind: completionTriggerKindMapping.monacoToVscode(
        context.triggerKind,
        onException,
      ),
      triggerCharacter: context.triggerCharacter,
    };
  },
  vscodeToMonaco(context, onException) {
    return {
      triggerKind: completionTriggerKindMapping.vscodeToMonaco(
        context.triggerKind,
        onException,
      ),
      triggerCharacter: context.triggerCharacter,
    };
  },
};
export const locationLinkMapper: Mapper<
  vscodeProtocol.LocationLink,
  monaco.languages.LocationLink
> = {
  vscodeToMonaco({
    targetUri,
    originSelectionRange,
    targetRange,
    targetSelectionRange,
  }) {
    return {
      uri: URI.parse(targetUri),
      originSelectionRange:
        originSelectionRange &&
        rangeMapping.vscodeToMonaco(originSelectionRange),
      range: rangeMapping.vscodeToMonaco(targetRange),
      targetSelectionRange: rangeMapping.vscodeToMonaco(targetSelectionRange),
    };
  },
  monacoToVscode({ range, uri, originSelectionRange, targetSelectionRange }) {
    const targetRange = rangeMapping.monacoToVscode(range);
    return {
      targetUri: uri.toString(true),
      originSelectionRange:
        originSelectionRange &&
        rangeMapping.monacoToVscode(originSelectionRange),
      targetRange,
      targetSelectionRange: targetSelectionRange
        ? rangeMapping.monacoToVscode(targetSelectionRange)
        : // TODO: what makes sense here as a defualt? monaco doesn't require it but vscode does
          targetRange,
    };
  },
};

export const locationMapper: Mapper<
  vscodeProtocol.Location,
  monaco.languages.Location
> = {
  vscodeToMonaco({ uri, range }) {
    return {
      uri: URI.parse(uri),
      range: rangeMapping.vscodeToMonaco(range),
    };
  },
  monacoToVscode({ uri, range }) {
    return {
      uri: uri.toString(true),
      range: rangeMapping.monacoToVscode(range),
    };
  },
};

export const codeActionKindMapping: Mapper<
  vscodeProtocol.CodeActionKind[] | undefined,
  string | undefined,
  [OnException],
  [OnException]
> = {
  vscodeToMonaco(codeActionKind, onException) {
    if (codeActionKind) {
      if (codeActionKind.length > 1) {
        onException(
          `${codeActionKind.length} codeAction kinds returned, using first`,
        );
      }
      return codeActionKind[0];
    } else {
      return codeActionKind;
    }
  },
  monacoToVscode(codeActionKind, onException) {
    if (
      codeActionKind === '' ||
      codeActionKind === 'quickfix' ||
      codeActionKind === 'refactor' ||
      codeActionKind === 'refactor.extract' ||
      codeActionKind === 'refactor.inline' ||
      codeActionKind === 'refactor.rewrite' ||
      codeActionKind === 'source' ||
      codeActionKind === 'source.organizeImports' ||
      codeActionKind === 'source.fixAll'
    ) {
      return [codeActionKind];
    } else {
      onException(`encountered unknown codeActionKind: '${codeActionKind}'`);
      return undefined;
    }
  },
};
export const codeActionContextMapping: Mapper<
  vscodeProtocol.CodeActionContext,
  monaco.languages.CodeActionContext,
  [OnException],
  [OnException]
> = {
  vscodeToMonaco(context, onException) {
    return {
      trigger: context.triggerKind
        ? codeActionTriggerKindMapping.vscodeToMonaco(
            context.triggerKind,
            onException,
          )
        : monaco.languages.CodeActionTriggerType.Invoke,
      markers: context.diagnostics.map((d) =>
        diagnosticsMapping.vscodeToMonaco(d, onException),
      ),
      only: codeActionKindMapping.vscodeToMonaco(context.only, onException),
    };
  },
  monacoToVscode(context, onException) {
    return {
      triggerKind: codeActionTriggerKindMapping.monacoToVscode(
        context.trigger,
        onException,
      ),
      diagnostics: context.markers.map((d) =>
        diagnosticsMapping.monacoToVscode(d, onException),
      ),
      only: codeActionKindMapping.monacoToVscode(context.only, onException),
    };
  },
};

export const commandMapping: Mapper<
  vscodeProtocol.Command,
  monaco.languages.Command
> = {
  vscodeToMonaco({ command, title, arguments: commandArguments }) {
    return {
      id: command,
      title,
      arguments: commandArguments,
    };
  },
  monacoToVscode({
    id,
    title,
    arguments: commandArguments,
    tooltip: _tooltip,
  }: monaco.languages.Command): vscodeProtocol.Command {
    return { command: id, title, arguments: commandArguments };
  },
};

export const textEditMapping: Mapper<
  vscodeProtocol.TextEdit,
  monaco.languages.TextEdit
> = {
  vscodeToMonaco({ newText, range }) {
    return {
      range: rangeMapping.vscodeToMonaco(range),
      text: newText,
    };
  },
  monacoToVscode({ range, text, eol: _eol }) {
    return {
      range: rangeMapping.monacoToVscode(range),
      newText: text,
    };
  },
};

export const workspaceFileEditMapping: Mapper<
  | vscodeProtocol.CreateFile
  | vscodeProtocol.RenameFile
  | vscodeProtocol.DeleteFile,
  monaco.languages.IWorkspaceFileEdit,
  [{ [id: string]: vscodeProtocol.ChangeAnnotation } | undefined]
> = {
  vscodeToMonaco(change, _changeAnnotations) {
    // TODO: we can use these values to go to monaco, but can't regenerate it coming back to vscode. Ignore for now!
    // const annotation = change.annotationId
    //   ? changeAnnotations?.[change.annotationId]
    //   : undefined;
    const annotation = undefined as vscodeProtocol.ChangeAnnotation | undefined;
    const metadata = annotation && {
      description: annotation.description,
      label: annotation.label,
      needsConfirmation: annotation.needsConfirmation ?? false,
    };
    if (change.kind === 'delete') {
      const {
        uri,
        kind: _kind,
        annotationId: _annotationId,
        options: { ignoreIfNotExists, recursive } = {},
      } = change;

      return {
        oldResource: monaco.Uri.parse(uri),
        newResource: undefined,
        options: { ignoreIfNotExists, recursive },
        metadata,
      };
    } else if (change.kind === 'create') {
      const {
        uri,
        kind: _kind,
        annotationId: _annotationId,
        options: { overwrite, ignoreIfExists } = {},
      } = change;

      return {
        newResource: monaco.Uri.parse(uri),
        oldResource: undefined,
        options: { overwrite, ignoreIfExists },
        metadata,
      };
    } else if (change.kind === 'rename') {
      const {
        newUri,
        oldUri,
        kind: _kind,
        annotationId: _annotationId,
        options: { overwrite, ignoreIfExists } = {},
      } = change;

      return {
        newResource: monaco.Uri.parse(newUri),
        oldResource: monaco.Uri.parse(oldUri),
        options: { overwrite, ignoreIfExists },
        metadata,
      };
    } else {
      return assertUnreachable(change);
    }
  },
  monacoToVscode({
    // annotation id is lost
    metadata: _metadata,
    newResource,
    oldResource,
    options,
  }) {
    if (!newResource && oldResource) {
      return {
        kind: 'delete',
        uri: oldResource.toString(true),
        annotationId: undefined,
        options,
      };
    } else if (!oldResource && newResource) {
      return {
        kind: 'create',
        uri: newResource.toString(true),
        annotationId: undefined,
        options,
      };
    } else if (oldResource && newResource) {
      return {
        kind: 'rename',
        newUri: newResource.toString(true),
        oldUri: oldResource.toString(true),
        annotationId: undefined,
        options,
      };
    } else {
      throw new Error('no files provided');
    }
  },
};

export const textDocumentEditMapping: Mapper<
  vscodeProtocol.TextDocumentEdit,
  monaco.languages.IWorkspaceTextEdit,
  [{ [id: string]: vscodeProtocol.ChangeAnnotation } | undefined],
  [],
  vscodeProtocol.TextDocumentEdit,
  monaco.languages.IWorkspaceTextEdit[]
> = {
  vscodeToMonaco({ edits, textDocument }, _changeAnnotations) {
    const { version, uri } = textDocument;
    return edits.map((edit) => {
      // TODO: we can use these values to go to monaco, but can't regenerate it coming back to vscode. Ignore for now!
      // const annotation =
      //   'annotationId' in edit && changeAnnotations
      //     ? changeAnnotations[edit.annotationId]
      //     : undefined;
      const annotation = undefined as
        | vscodeProtocol.ChangeAnnotation
        | undefined;

      return {
        textEdit: textEditMapping.vscodeToMonaco(edit),
        resource: monaco.Uri.parse(uri),
        versionId: version ?? undefined,
        metadata: annotation && {
          description: annotation.description,
          label: annotation.label,
          needsConfirmation: annotation.needsConfirmation ?? false,
        },
      };
    });
  },
  monacoToVscode({
    resource,
    textEdit,
    versionId,
    // annotationId lost so we can't add it back
    metadata: _metadata,
  }: monaco.languages.IWorkspaceTextEdit): vscodeProtocol.TextDocumentEdit {
    return {
      edits: [textEditMapping.monacoToVscode(textEdit)],
      textDocument: {
        version: versionId ?? null,
        uri: resource.toString(true),
      },
    };
  },
};

export const workspaceEditMapping: Mapper<
  vscodeProtocol.WorkspaceEdit,
  monaco.languages.WorkspaceEdit
> = {
  vscodeToMonaco({ changeAnnotations, changes, documentChanges }) {
    const changesEdits = Object.entries(changes ?? {}).flatMap(
      ([uri, fileChanges]) =>
        fileChanges.map((change) => ({
          textEdit: textEditMapping.vscodeToMonaco(change),
          resource: monaco.Uri.parse(uri),
          versionId: undefined,
          metadata: undefined,
        })),
    );
    const monacoDocumentChanges =
      documentChanges?.flatMap<monaco.languages.IWorkspaceFileEdit>(
        (change) => {
          if ('kind' in change) {
            return workspaceFileEditMapping.vscodeToMonaco(
              change,
              changeAnnotations,
            );
          } else {
            return textDocumentEditMapping.vscodeToMonaco(
              change,
              changeAnnotations,
            );
          }
        },
      ) ?? [];

    return {
      edits: [...changesEdits, ...monacoDocumentChanges],
    };
  },
  monacoToVscode({ edits }) {
    return edits.reduce((acc: vscodeProtocol.WorkspaceEdit, edit) => {
      if ('resource' in edit) {
        if (edit.versionId || edit.metadata) {
          const documentChanges = acc.documentChanges ?? [];
          documentChanges.push(textDocumentEditMapping.monacoToVscode(edit));
          acc.documentChanges = documentChanges;
        } else {
          const changes = acc.changes ?? {};
          const {
            resource,
            textEdit,
            versionId: _versionId,
            metadata: _metadata,
          } = edit;
          const uriEdits = changes[resource.toString(true)] ?? [];
          uriEdits.push(textEditMapping.monacoToVscode(textEdit));
          changes[resource.toString(true)] = uriEdits;
          acc.changes = changes;
        }
      } else {
        const documentChanges = acc.documentChanges ?? [];
        documentChanges.push(workspaceFileEditMapping.monacoToVscode(edit));
        acc.documentChanges = documentChanges;
      }
      return acc;
    }, {}); // changeAnnotations are lost
  },
};

export const codeActionMapping: Mapper<
  vscodeProtocol.CodeAction,
  monaco.languages.CodeAction,
  [OnException],
  [OnException]
> = {
  vscodeToMonaco(
    {
      title,
      command,
      data: _data, // TODO: This data will be lost, is there a client capability to say we don't support it?
      diagnostics,
      disabled,
      edit,
      isPreferred,
      kind,
    },
    onException,
  ) {
    return {
      title,
      command: command ? commandMapping.vscodeToMonaco(command) : command,
      diagnostics: diagnostics?.map((d) =>
        diagnosticsMapping.vscodeToMonaco(d, onException),
      ),
      disabled: disabled?.reason,
      edit: edit ? workspaceEditMapping.vscodeToMonaco(edit) : edit,
      isPreferred,
      kind,
    };
  },
  monacoToVscode(
    { title, command, diagnostics, disabled, edit, isPreferred, kind },
    onException,
  ) {
    return {
      title,
      command: command ? commandMapping.monacoToVscode(command) : command,
      diagnostics: diagnostics?.map((d) =>
        diagnosticsMapping.monacoToVscode(d, onException),
      ),
      disabled: disabled === undefined ? undefined : { reason: disabled },
      edit: edit ? workspaceEditMapping.monacoToVscode(edit) : edit,
      isPreferred,
      kind,
    };
  },
};
